import {Component, EventEmitter, Input, Output} from '@angular/core';

/**
 * Interactive integer star input (1–5) plus a "Clear" affordance.
 *
 * Uses daisyUI's `rating` radios, which give integer snapping and
 * keyboard/radio semantics for free — the right tool for personal input, unlike
 * the fractional read-only {@link UiStarBarComponent}.
 *
 * Stateless: it renders `score` and emits intent. The parent owns the
 * optimistic recompute + rollback of the aggregate.
 */
@Component({
  selector: 'app-rating-input',
  standalone: true,
  imports: [],
  template: `
    <div class="flex items-center gap-2">
      <div class="rating" [class.rating-sm]="size === 'sm'" [class.rating-lg]="size === 'lg'"
           [class.pointer-events-none]="disabled" [class.opacity-50]="disabled">
        @for (n of stars; track n) {
          <input
            type="radio"
            [name]="name"
            class="mask mask-star-2 bg-warning"
            [attr.aria-label]="n + ' star'"
            [checked]="score === n"
            [disabled]="disabled"
            (change)="onPick(n)"
          />
        }
      </div>

      @if (score > 0) {
        <button
          type="button"
          class="btn btn-ghost btn-xs text-base-content/60"
          [disabled]="disabled"
          (click)="clear.emit()">
          Clear
        </button>
      }
    </div>
  `,
})
export class RatingInputComponent {
  readonly stars = [1, 2, 3, 4, 5];

  /** The current integer score, 0 when unrated. */
  @Input() score = 0;

  /** Disable interaction while a request is in flight. */
  @Input() disabled = false;

  /** Radio group name — must be unique per input instance on the page. */
  @Input() name = 'my-rating';

  /** Star size. */
  @Input() size: 'sm' | 'md' | 'lg' = 'md';

  /** Emits the chosen integer score (1–5). */
  @Output() scoreChange = new EventEmitter<number>();

  /** Emits when the user clears their rating. */
  @Output() clear = new EventEmitter<void>();

  onPick(n: number): void {
    if (this.disabled) return;
    this.scoreChange.emit(n);
  }
}
